import { useMutation } from "react-query";
import HttpService from "../../services/http";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import i18next from "i18next";
import { queryClient } from "../../providers/QueryProvider";

// The server deletes only the files this account uploaded: the others stay and are listed as not deleted.
export const notDeletedFiles = (response: any): string[] => {
  const files = response?.data?.element?.not_deleted;
  return Array.isArray(files) ? files : [];
};

export const toastNotDeletedFiles = (files: string[]) =>
  toast.warning(
    i18next.t("fileDeletion.notDeleted", {
      count: files.length,
      files: files.join(", "),
    }) as string
  );

/** The server answers in English: the dashboard tells a refused upload or deletion from its status, once. */
export const cdnErrorKey = (
  err: AxiosError<any, any>,
  scope: "imageUpload" | "fileDeletion"
): string => {
  if (err.response?.status === 402) return `${scope}.planRequired`;
  if (err.response?.status === 403) return `${scope}.forbidden`;
  return `${scope}.failed`;
};

const toastCdnError =
  (scope: "imageUpload" | "fileDeletion") => (err: AxiosError<any, any>) => {
    const key = cdnErrorKey(err, scope);
    toast.error(i18next.t(key) as string, { toastId: key });
  };

export const useUpdateAvatar = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      data,
    }: {
      user_identification: string;
      entity_identification: string;
      data: any;
    }) =>
      HttpService.post(
        `/${user_identification}/${entity_identification}/cdn/avatar`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      ),
    {
      onSuccess: () => {
        toast.success(i18next.t("imageUpload.updated") as string);
        queryClient.invalidateQueries("profile");
      },
      onError: toastCdnError("imageUpload"),
    }
  );
};

export const useCdvAvatar = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
    }: {
      entity_identification: string;
      data: any;
    }) =>
      HttpService.post(
        `/link-registration/${entity_identification}/avatar`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
  );
};

/** A refused upload, told from its status or code: RSPYKO001 is a file refused for its size or format. */
export const uploadErrorKey = (err: AxiosError<any, any>): string => {
  if (err.response?.status === 402) return "fileUpload.planRequired";
  if (err.response?.status === 403) return "fileUpload.forbidden";
  if (err.response?.data?.code === "RSPYKO001") return "fileUpload.rejected";
  return "fileUpload.failed";
};

const toastUploadError = (err: AxiosError<any, any>) => {
  const key = uploadErrorKey(err);
  toast.error(i18next.t(key) as string, { toastId: key });
};

/** Tells a refused upload once, in the language of the dashboard; `notify: false` for a page that tells it itself. */
export const useUploadFile = ({ notify = true }: { notify?: boolean } = {}) => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      data,
    }: {
      user_identification: string;
      entity_identification: string;
      data: any;
    }) =>
      HttpService.post(
        `/${user_identification}/${entity_identification}/cdn/file`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      ),
    {
      onError: (err) => {
        if (notify) toastUploadError(err);
      },
    }
  );
};

/** `notify: false` for a clean-up the user did not ask for, such as the avatar a new one replaces. */
export const useDeleteFiles = ({
  notify = true,
}: { notify?: boolean } = {}) => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      files,
    }: {
      user_identification: string;
      entity_identification: string;
      files: string[];
    }) =>
      HttpService.delete(
        `/${user_identification}/${entity_identification}/cdn/files`,
        {
          data: { files },
        }
      ),
    {
      onSuccess: (response) => {
        if (!notify) return;
        const notDeleted = notDeletedFiles(response);
        if (notDeleted.length > 0) toastNotDeletedFiles(notDeleted);
        else toast.success(i18next.t("fileDeletion.deleted") as string);
      },
      onError: (err) => {
        if (notify) toastCdnError("fileDeletion")(err);
      },
    }
  );
};

/** As useUploadFile, for a private file. */
export const usePrivateUploadFile = ({
  notify = true,
}: { notify?: boolean } = {}) => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      data,
      token,
    }: {
      user_identification: string;
      entity_identification: string;
      data: any;
      token?: string;
    }) =>
      HttpService.post(
        `/${user_identification}/${entity_identification}/cdn/generate-upload-url`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(token && { Authorization: token }),
          },
        }
      ),
    {
      onError: (err) => {
        if (notify) toastUploadError(err);
      },
    }
  );
};

type PrivateFile = {
  user_identification: string;
  entity_identification: string;
  file_name: string;
};

export const useGetPrivateFile = () =>
  useMutation<any, AxiosError<any, any>, PrivateFile>(
    ({ user_identification, entity_identification, file_name }) =>
      HttpService.get(
        `/${user_identification}/${entity_identification}/cdn/get-file?file_name=${file_name}`
      )
  );

/** The server answers in English: a file of another account, or one the CDN no longer has, is told as such. */
export const privateFileErrorKey = (err: AxiosError<any, any>): string => {
  if (err.response?.status === 403) return "fileOpen.notYours";
  if (err.response?.data?.code === "RSCTKO025") return "fileOpen.missing";
  return "fileOpen.failed";
};

const toastFileOpenError = (key: string) =>
  toast.error(i18next.t(key) as string, { toastId: key });

/**
 * Opens a private file in a new tab, opened at once so that the browser does not block it, and closed again when
 * the file cannot be opened: then a single toast tells why.
 */
export const useOpenPrivateFile = () => {
  const { mutate, isLoading } = useGetPrivateFile();
  const open = (file: PrivateFile) => {
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    mutate(file, {
      onSuccess: (res) => {
        const url = res?.data?.element?.url;
        if (url && tab) {
          tab.location.href = url;
          return;
        }
        tab?.close();
        toastFileOpenError("fileOpen.failed");
      },
      onError: (err) => {
        tab?.close();
        toastFileOpenError(privateFileErrorKey(err));
      },
    });
  };
  return { open, isOpening: isLoading };
};
